import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CookieConsent {
  essential: boolean;     // Always true, cannot be disabled
  analytics: boolean;     // Optional, defaults to false
  marketing: boolean;     // Optional, defaults to false
  preferences: boolean;   // Optional, defaults to false
}

export interface CookieConsentWithTimestamp extends CookieConsent {
  timestamp: number;
  version: string;
}

interface CookieConsentContextType {
  consent: CookieConsent;
  showBanner: boolean;
  isLoading: boolean;
  hasDoNotTrack: boolean;
  acceptEssentialOnly: () => void;
  acceptAll: () => void;
  updateConsent: (newConsent: Partial<CookieConsent>) => void;
  withdrawConsent: () => void;
  hasConsent: (type: keyof CookieConsent) => boolean;
  customizeConsent: (newConsent: CookieConsent) => void;
  openBanner: () => void;
  closeBanner: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_STORAGE_KEY = 'gratimy-cookie-consent';
const CONSENT_VERSION = '1.0';
const CONSENT_EXPIRY_DAYS = 365; // 1 year

// Default consent (GDPR compliant - opt-in only)
const DEFAULT_CONSENT: CookieConsent = {
  essential: true,      // Always required
  analytics: false,     // Opt-in required
  marketing: false,     // Opt-in required
  preferences: false,   // Opt-in required
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [consent, setConsent] = useState<CookieConsent>(DEFAULT_CONSENT);
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user has "Do Not Track" enabled
  const hasDoNotTrack = () => {
    return navigator.doNotTrack === '1' || 
           (window as any).doNotTrack === '1' || 
           (navigator as any).msDoNotTrack === '1';
  };

  // Load consent from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const parsed: CookieConsentWithTimestamp = JSON.parse(stored);
        
        // Check if consent is still valid (not expired and same version)
        const isValid = 
          parsed.timestamp && 
          parsed.version === CONSENT_VERSION &&
          (Date.now() - parsed.timestamp) < (CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
        
        if (isValid) {
          // Use stored consent
          setConsent({
            essential: true, // Always ensure essential is true
            analytics: hasDoNotTrack() ? false : parsed.analytics,
            marketing: hasDoNotTrack() ? false : parsed.marketing,
            preferences: parsed.preferences,
          });
          setShowBanner(false);
        } else {
          // Consent expired or invalid, show banner
          setShowBanner(true);
        }
      } else {
        // No stored consent, show banner
        setShowBanner(true);
      }
    } catch (error) {
      setShowBanner(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save consent to localStorage
  const saveConsent = (newConsent: CookieConsent) => {
    try {
      const consentWithTimestamp: CookieConsentWithTimestamp = {
        ...newConsent,
        essential: true, // Always ensure essential is true
        analytics: hasDoNotTrack() ? false : newConsent.analytics,
        marketing: hasDoNotTrack() ? false : newConsent.marketing,
        timestamp: Date.now(),
        version: CONSENT_VERSION,
      };
      
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consentWithTimestamp));
      setConsent(consentWithTimestamp);
      setShowBanner(false);
      
      // Dispatch custom event for analytics integration
      window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
        detail: consentWithTimestamp 
      }));
    } catch (error) {
      // Consent save failed silently
    }
  };

  // Accept only essential cookies (GDPR compliant default)
  const acceptEssentialOnly = () => {
    saveConsent(DEFAULT_CONSENT);
  };

  // Accept all cookies
  const acceptAll = () => {
    const allConsent: CookieConsent = {
      essential: true,
      analytics: !hasDoNotTrack(),
      marketing: !hasDoNotTrack(),
      preferences: true,
    };
    saveConsent(allConsent);
  };

  // Update specific consent
  const updateConsent = (newConsent: Partial<CookieConsent>) => {
    const updatedConsent: CookieConsent = {
      ...consent,
      ...newConsent,
      essential: true, // Always ensure essential is true
    };
    saveConsent(updatedConsent);
  };

  // Withdraw consent (show banner again)
  const withdrawConsent = () => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      setConsent(DEFAULT_CONSENT);
      setShowBanner(true);
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('cookieConsentWithdrawn'));
    } catch (error) {
      // Consent withdrawal failed silently
    }
  };

  // Check if specific consent is given
  const hasConsent = (type: keyof CookieConsent): boolean => {
    return consent[type] === true;
  };

  // Open banner for cookie management (GDPR requirement)
  const openBanner = () => {
    setShowBanner(true);
  };

  // Close banner
  const closeBanner = () => {
    setShowBanner(false);
  };

  const contextValue: CookieConsentContextType = {
    consent,
    showBanner,
    isLoading,
    hasDoNotTrack: hasDoNotTrack(),
    acceptEssentialOnly,
    acceptAll,
    updateConsent,
    withdrawConsent,
    hasConsent,
    customizeConsent: saveConsent,
    openBanner,
    closeBanner,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}